import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Select from 'react-select';
import './ContactPage.css';
import '../components/BackgroundWrapper';
import BackgroundWrapper from '../components/BackgroundWrapper';
import { useSendContactEmailMutation } from '../apiSlice';
import {isWorkEmail} from '../utils/emailValidation';
import * as Yup from 'yup';

interface RoleOption {
  value: string;
  label: string;
}

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    role: '',
    inquiry: ''
  });
  const [sendContactEmail, { isLoading }] = useSendContactEmailMutation();
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const roles: RoleOption[] = [
    {value: 'security-leader', label: 'Security Leader'},
    {value: 'security-engineer', label: 'Security Engineer'},
    {value: 'platform-engineer', label: 'Platform Engineer'},
    {value: 'engineering-leader', label: 'Engineering Leader'},
    {value: 'ai-engineer', label: 'AI Engineer'},
    {value: 'data-scientist', label: 'Data Scientist'},
    {value: 'product-manager', label: 'Product Manager'},
    {value: 'compliance-and-risk', label: 'Compliance and Risk'},
    {value: 'legal-and-privacy', label: 'Legal and Privacy'},
    {value: 'operations-and-procurement', label: 'Operations and Procurement'},
    {value: 'ciso', label: 'CISO'},
    {value: 'cio', label: 'CIO'},
    {value: 'cdo', label: 'CDO'},
    {value: 'ai-security-researcher', label: 'AI Security Researcher'},
    {value: 'other', label: 'Other'},
  ];

  // Yup validation schema
  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('Email is required')
      .test('is-work-email', 'Please use a work email address. Personal emails are not accepted.', 
        (value) => value ? isWorkEmail(value) : false
      ),
  });

  const validateEmail = async (email: string) => {
    try {
      await validationSchema.validateAt('email', { email });
      setEmailError(null);
      return true;
    } catch (err: any) {
      setEmailError(err.message);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); //Prevent from reloading the page, preserving the state and use js for the submission instead.

    const isEmailValid = await validateEmail(formData.email);
    if (!isEmailValid) {
      return;
    }

    try{
      await sendContactEmail(formData).unwrap();
      setError(null);
      setEmailError(null);
      navigate('/contacted');
    } catch(err: any){
       if (err?.data?.detail && Array.isArray(err.data.detail)) {
        const messages = err.data.detail.map((d: any) => d.msg).join(', ');
        setError(messages);
      } else if (err?.data?.detail) {
        setError(err.data.detail);
      } else {
        setError('Failed to send message. Please try again.');
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleEmailBlur = () => {
    if (formData.email) {
      validateEmail(formData.email);
    }
  };

  const handleRoleChange = (selectedOption: RoleOption) => {
    setFormData({
      ...formData,
      role: selectedOption ? selectedOption.value : ''
    });
  };

  return (
    <BackgroundWrapper>
      <div className="contact-container">
        <Header showContactButton={false} />

        <main className="contact-main">
          {/* Left side */}
          <div className="contact-left">
            <h1 className="contact-title">
              Get In Touch
            </h1>
            <p className="contact-subtitle">
              Reach out to learn more about DeepKeep AI Security Platform
            </p>
          </div>

          {/* Right side - Contact form */}
          <div className="contact-right">
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="contact-form-row">
                <div className="contact-form-group">
                  <input
                    type="text"
                    name="firstName"
                    placeholder="* First Name"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="contact-input"
                  />
                </div>
                <div className="contact-form-group">
                  <input
                    type="text"
                    name="lastName"
                    placeholder="* Last Name"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="contact-input"
                  />
                </div>
              </div>

              <div className="contact-form-group">
                <input
                  type="email"
                  name="email"
                  placeholder="* Work Email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleEmailBlur} 
                  required
                  className={`contact-input ${emailError ? 'contact-input-error' : ''}`}
                />
                {emailError && <div className="contact-field-error">{emailError}</div>}
              </div>

              <div className="contact-form-group">
                <input
                  type="text"
                  name="company"
                  placeholder="Company"
                  value={formData.company}
                  onChange={handleChange}
                  className="contact-input"
                />
              </div>

              <div className="contact-form-group">
                <Select
                  name="role"
                  value={roles.find((role: {value: string;}) => role.value === formData.role)}
                  onChange={handleRoleChange}
                  options={roles}
                  placeholder="* Role"
                  className="contact-react-select"
                  classNamePrefix="contact-select"
                  isClearable
                  required
                />
              </div>

              <div className="contact-form-group">
                <textarea
                  name="inquiry"
                  placeholder="Inquiry"
                  value={formData.inquiry}
                  onChange={handleChange}
                  rows={5}
                  className="contact-textarea"
                  required
                />
              </div>

              <div className="contact-form-group">
                {error && <div className="contact-error">{error}</div>}

                <button
                  type="submit"
                  className="contact-submit-button"
                >
                  Get in Touch
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </BackgroundWrapper>
  );
};

export default ContactPage;